import { Injectable, NgZone } from '@angular/core';
import { HttpService } from './http.service';
import { AbstractControl, FormGroup } from '@angular/forms';
import { NzIconService } from 'ng-zorro-antd/icon';
import { hezky_svg_data } from '../hezky_svg_data';
import { Router } from '@angular/router';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { API_ENDPOINTS } from '../../core/services/api.service';
import { combineLatest, map, distinctUntilChanged, filter, Observable, of, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { environment } from '../../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';


export interface NzTreeNode {
  level: number;
  title: string;
  key: string;
  children?: NzTreeNode[];
  isLeaf?: boolean;
  expanded?: boolean;
  extraData?: any;
  formData?: any,
  ancestors?: any[];
  type: 'location' | 'group';
  parent?: any;
  expandable: boolean; // Add this property
}
export interface FlatNode {
  title: string;
  key: string;
  isLeaf?: boolean;
  children?: FlatNode[];
  expandable: boolean;
  level: number;
}
export interface SpocDetail {
  name: string;
  email: string;
  phone: string;
  designation: string;
}

export interface Location {
  location_id?: number;
  registered_name: string;
  ancestors: string[];
  spoc_detail: SpocDetail[];
  display_name: string;
  registered_address: string | null;
  customer_id: number;
  is_deleted: boolean;
}

@UntilDestroy({ checkProperties: true })
@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private userEmailSubject = new BehaviorSubject<string>('');
  userEmail$ = this.userEmailSubject.asObservable();

  constructor(public httpSvc: HttpService,
    public router: Router,
    public drawerSvc: NzDrawerService,
    public message: NzMessageService,
    public notification: NzNotificationService,
    public modalSvc: NzModalService,
    private ngZone: NgZone,
    private iconService: NzIconService) {
    this.initializeCustomIcons();
  }

  private validateFormGroup(form: FormGroup): void {
    Object.values(form.controls).forEach((formControl: AbstractControl) => {
      formControl.markAsTouched();
      formControl.markAsDirty();
      formControl.updateValueAndValidity({ onlySelf: true });
    });
  }

  private initializeCustomIcons(): void {
    this.ngZone.runOutsideAngular(() => {
      hezky_svg_data.forEach((item: { name: string; value: string }) => {
        this.iconService.addIconLiteral(`hezky:${item.name}`, item.value);
      });
    });
  }

  public validate(form: FormGroup): boolean {
    if (form.invalid) {
      this.validateFormGroup(form);
      this.message.info('Please fill all required fields!');
      return false;
    }
    return true;
  }

  public trackByFn(index: number, item: any): any {
    return item.id ?? index;
  }

  public getLocation(customerId: number): Observable<any> {
    const headers = new HttpHeaders({ 'x-customer-id': customerId });
    return this.httpSvc.get(environment.apiBaseUrl + API_ENDPOINTS.CUSTOMER.LOCATION.GET_LOCATION + `?tree=true`, headers);
  }

  getLocationList(customerId: any): Observable<any> {
    const headers = new HttpHeaders({ 'x-customer-id': customerId });
    return this.httpSvc.get(environment.apiBaseUrl + API_ENDPOINTS.CUSTOMER.LOCATION.GET_LOCATION, headers);
  }

  public convertToNzTreeSelectFormat(data: any[]): NzTreeNode[] {
    const result: NzTreeNode[] = [];
    if (!data || !Array.isArray(data)) {
      return result;
    }

    // Recursive function to process groups and their children/locations
    const processGroup = (group: any, parentNode: NzTreeNode | null, level: number): NzTreeNode => {
      const groupNode: NzTreeNode = {
        title: group.group_name,
        key: `group-${group.group_id}`,
        children: [],
        isLeaf: false,
        extraData: {
          group_id: group.group_id,
          group_name: group.group_name,
          parent_group_id: group.parent_group_id
        },
        level: level,
        type: 'group',
        expandable: true,
        parent: parentNode || undefined
      };

      // Process locations under this group
      if (group.locations && Array.isArray(group.locations)) {
        group.locations.forEach((location: any) => {
          if (!location || !location.display_name || !location.location_id) return;

          const locationNode: NzTreeNode = {
            title: location.display_name,
            key: `${location.location_id}`,
            isLeaf: true,
            extraData: location,
            level: level + 1,
            type: 'location',
            expandable: false,
            parent: groupNode
          };
          groupNode.children!.push(locationNode);
        });
      }

      // Process child groups recursively
      if (group.children && Array.isArray(group.children)) {
        group.children.forEach((childGroup: any) => {
          const childNode = processGroup(childGroup, groupNode, level + 1);
          groupNode.children!.push(childNode);
        });
      }

      // Update expandable based on whether there are children
      groupNode.expandable = !!(groupNode.children && groupNode.children.length > 0);

      return groupNode;
    };

    // Process each top-level group
    data.forEach((group: any) => {
      if (group.group_id !== undefined && group.group_name) {
        // It's a group object with the new structure
        const groupNode = processGroup(group, null, 0);
        result.push(groupNode);
      } else if (group.location_id && group.display_name) {
        // It's a standalone location (no group)
        const locationNode: NzTreeNode = {
          title: group.display_name,
          key: `${group.location_id}`,
          isLeaf: true,
          extraData: group,
          level: 0,
          type: 'location',
          expandable: false,
          parent: undefined
        };
        result.push(locationNode);
      }
    });

    return result;
  }
  addLocation(data: any): Observable<any> {
    return this.httpSvc.post<any[]>(environment.apiBaseUrl + API_ENDPOINTS.CUSTOMER.LOCATION.ADD_LOCATION, data)
  }
  updateLocation(id: number | string, data: any): Observable<any> {
    return this.httpSvc.patch<any[]>(environment.apiBaseUrl + API_ENDPOINTS.CUSTOMER.LOCATION.UPDATE_LOCATION, id, data)
  }

  setUserEmail(email: string): void {
    this.userEmailSubject.next(email);
  }

  /**
   * Compares two objects and returns an object containing only the changed fields
   * @param original - The original object (before changes)
   * @param modified - The modified object (after changes)
   * @param excludeFields - Optional array of field names to exclude from comparison
   * @returns Object containing only the changed fields
   */
  public getChangedFields(original: any, modified: any, excludeFields: string[] = []): any {
    const changes: any = {};

    // Handle null or undefined cases
    if (!original || !modified) {
      return changes;
    }

    // Iterate through all keys in the modified object
    Object.keys(modified).forEach((key) => {
      // Skip excluded fields
      if (excludeFields.includes(key)) {
        return;
      }

      const originalValue = original[key];
      const modifiedValue = modified[key];
      if (originalValue === null && modifiedValue === null) return;
      if (originalValue === undefined && modifiedValue === undefined) return;

      let hasChanged = false;

      if (originalValue === null || originalValue === undefined) {
        hasChanged = true;
      } else if (modifiedValue === null || modifiedValue === undefined) {
        hasChanged = true;
      } else if (Array.isArray(originalValue) && Array.isArray(modifiedValue)) {
        // Handle arrays
        hasChanged = originalValue.length !== modifiedValue.length ||
          JSON.stringify(originalValue) !== JSON.stringify(modifiedValue);
      } else if (typeof originalValue === 'object' && typeof modifiedValue === 'object') {
        // Handle objects
        hasChanged = JSON.stringify(originalValue) !== JSON.stringify(modifiedValue);
      } else {
        // Handle primitives
        hasChanged = originalValue !== modifiedValue;
      }

      if (hasChanged) {
        changes[key] = modifiedValue;
      }
    });

    return changes;
  }
  public parseJsonArray(value: any): any[] {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try { return JSON.parse(value); } catch { return []; }
    }
    return [];
  }
}
